import React, { useCallback, useEffect, useState } from "react";
import Stepper from "../../components/Stepper";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from "react-router-dom";
import {
  IconArrowNarrowLeftDashed,
  IconArrowNarrowRightDashed,
} from "@tabler/icons-react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";
import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS } from "../../constant/_constant";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  Form,
  Alert,
  Spinner,
  InputGroup,
  DropdownDivider,
  Container,
} from "react-bootstrap";
import GeneralStep from "./GeneralStep";
import BillingInfo from "./BillingInfo";
import Attachment from "../RFQ/Attachment";
import ComanConfirmationModal from "../../components/ComanConfirmationModal";
import SuccsessMsgModal from "../../components/SuccessMsgModal";
import { transformData } from "../../utils/_helpers";
import FileUpload from "../../components/Attachment/Attachment";
import ErrorModal from "./ErrorModal";
import SuccessModal from "./SuccessModal";
import auth_background from "../../assets/auth_background.webp";
import useIdleLogout from "../../hooks/useIdleLogout";

const generateUniqueId = () => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  let result = "";
  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }

  const timestamp = Date.now().toString();
  const uniqueId = result + timestamp.slice(-4);
  return uniqueId;
};

const urlRegex = /^(https?:\/\/|www\.)[\w-]+(\.[\w-]+)+([/?#].*)?$/i;

const validationSchema = Yup.object().shape({
  display_name: Yup.string().required("Display Name is required"),
  first_name: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  last_name: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  shipping_name: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  shipping_email: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .email("Invalid email format")
    .optional(),
  shipping_phone: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .test("valid-phone", "Invalid phone number", (value) => {
      if (!value) {
        return true;
      }
      if (typeof value === "string" && value.trim() !== "") {
        return isValidPhoneNumber(value);
      }
      return false;
    })
    .optional(),

  billing_name: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  billing_email: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .email("Invalid email format")
    .optional(),
  shipping_phone: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .test("valid-phone", "Invalid phone number", (value) => {
      if (!value) {
        return true;
      }
      if (typeof value === "string" && value.trim() !== "") {
        return isValidPhoneNumber(value);
      }
      return false;
    })
    .optional(),

  primary_email: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .email("Invalid email format")
    .optional(),

  primary_phone: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .test("valid-phone", "Invalid phone number", (value) => {
      if (!value) {
        return true;
      }
      if (typeof value === "string" && value.trim() !== "") {
        return isValidPhoneNumber(value);
      }
      return false;
    })
    .optional(),

  // Step 2

  Bill_address_1: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  Bill_address_2: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  Bill_city: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  Bill_state: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  Bill_zip_code: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .min(5, "minimum 5 digits")
    .max(10, "maximum 10 digits")
    .optional(),
  Bill_country: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),

  // shipping

  Ship_address_1: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  Ship_address_2: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  Ship_city: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  Ship_state: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
  Ship_zip_code: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .min(5, "minimum 5 digits")
    .max(10, "maximum 10 digits")
    .optional(),
  Ship_country: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),

  // web_addr: Yup.string()
  //   .transform((value) => (value === "" ? null : value))
  //   .nullable()
  //   .url("Please enter a valid URL")
  //   .optional(),
  web_addr: Yup.string()
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .test(
      "is-valid-url",
      "Please enter a valid URL",
      (value) => !value || urlRegex.test(value)
    )
    .optional(),
});

function CreateCustomer() {
  const PORT = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const steps = ["General", "Billing & Shipping", "Attachments"];
  const [currentStep, setCurrentStep] = useState(1);
  const [formId, setFormId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [customerDataFetched, setCustomerDataFetched] = useState(false);
  const [Type, setType] = useState("INDIVIDUAL");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [tokenError, setTokenError] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState({
    show: false,
    message: "",
    btntext: "",
    title: "",
  });
  const [isModalCnfirmOpen, setIsModalCnfirmOpen] = useState({
    show: false,
    action: null,
    message: "",
  });

  useIdleLogout();
  const { token } = useParams();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  console.log("token", token);

  const {
    handleSubmit,
    setError,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = methods;

  console.log("errors", errors);

  const { data: customer, postData: fetchCustomer } = usePostRequest(
    API_ENDPOINTS.GET_SINGLE_CUSTOMER
  );
  const { isLoading, postData: uploadImage } = usePostRequest(
    API_ENDPOINTS.UPLOAD_ATTACHMENTS
  );

  const { isLoadingquickbook, postData: WihtouthQuickBook } = usePostRequest(
    API_ENDPOINTS.ADD_PUBLIC_CUSTOMER
  );

  const { isLoading: createLoading, postData } = usePostRequest(
    API_ENDPOINTS.ADD_PUBLIC_CUSTOMER
  );

  const { postData: verifyToken } = usePostRequest(
    API_ENDPOINTS.VERIFY_REGISTER_TOKEN
  );

  useEffect(() => {
    if (token) {
      verifyToken(
        { token },
        () => setTokenError(null),
        (error) => setTokenError(error?.message || "Token verification failed.")
      );
    }
  }, [token]);

  useEffect(() => {
    //  customer?.data?.type ? setType(customer?.data?.type),
    if (customer) {
      console.log(customer, "customer");

      if (customer?.data?.attachment_id) {
        setFormId(customer?.data?.attachment_id || formId);
      }
      setValue("display_name", customer?.data?.display_name || null);
      setValue("first_name", customer?.data?.first_name || null);
      setValue("last_name", customer?.data?.last_name || null);
      setValue("Address", customer?.data?.address || null);
      setValue("CustomerType", customer?.data?.type || null);
      if (customer?.data?.type) {
        setType(customer?.data?.type);
      }
      setValue(
        "web_addr",
        customer?.data?.web_addr
          ? JSON.parse(customer?.data?.web_addr).URI
          : null
      );
      setValue("billing_email", customer?.data?.billing_email || null);
      setValue("billing_name", customer?.data?.billing_name || null);
      setValue("billing_phone", customer?.data?.billing_phone || null);
      setValue("shipping_email", customer?.data?.shipping_email || null);
      setValue("shipping_name", customer?.data?.shipping_name || null);
      setValue("shipping_phone", customer?.data?.shipping_phone || null);
      setValue(
        "primary_email",
        customer?.data?.primary_email
          ? JSON.parse(customer?.data?.primary_email).Address
          : null
      );

      setValue(
        "primary_phone",
        customer?.data?.primary_phone
          ? JSON.parse(customer?.data?.primary_phone).FreeFormNumber
          : null
      );

      setValue(
        "Bill_city",
        customer?.data?.billing_address
          ? JSON.parse(customer?.data?.billing_address).City
          : null
      );

      setValue(
        "Bill_address_1",
        customer?.data?.billing_address
          ? JSON.parse(customer?.data?.billing_address).Line1
          : null
      );

      setValue(
        "Bill_address_2",
        customer?.data?.billing_address
          ? JSON.parse(customer?.data?.billing_address).Line2
          : null
      );
      setValue(
        "Bill_country",
        customer?.data?.billing_address
          ? JSON.parse(customer?.data?.billing_address).Country
          : null
      );

      setValue(
        "Bill_zip_code",
        customer?.data?.billing_address
          ? JSON.parse(customer?.data?.billing_address).PostalCode
          : null
      );
      setValue(
        "Bill_state",
        customer?.data?.billing_address
          ? JSON.parse(customer?.data?.billing_address).CountrySubDivisionCode
          : null
      );

      setValue(
        "Ship_city",
        customer?.data?.shipping_address
          ? JSON.parse(customer?.data?.shipping_address).City
          : null
      );
      setValue(
        "Ship_address_1",
        customer?.data?.shipping_address
          ? JSON.parse(customer?.data?.shipping_address).Line1
          : null
      );
      setValue(
        "Ship_address_2",
        customer?.data?.shipping_address
          ? JSON.parse(customer?.data?.shipping_address).Line2
          : null
      );
      setValue(
        "Ship_country",
        customer?.data?.shipping_address
          ? JSON.parse(customer?.data?.shipping_address).Country
          : null
      );
      setValue(
        "Ship_zip_code",
        customer?.data?.shipping_address
          ? JSON.parse(customer?.data?.shipping_address).PostalCode
          : null
      );
      setValue(
        "Ship_state",
        customer?.data?.shipping_address
          ? JSON.parse(customer?.data?.shipping_address).CountrySubDivisionCode
          : null
      );
    }
  }, [customer, setValue]);

  const getFieldsForCurrentStep = (step) => {
    switch (step) {
      case 1:
        return [
          "display_name",
          "primary_phone",
          "web_addr",
          "first_name",
          "last_name",
          "primary_email",
          "primary_phone",
          "Address",
        ];
      case 2:
        return [
          "billing_name",
          "billing_email",
          "billing_phone",
          "Bill_address_1",
          "Bill_address_2",
          "Bill_city",
          "Bill_state",
          "Bill_zip_code",
          "Bill_country",
          "shipping_name",
          "shipping_email",
          "shipping_phone",
          "Ship_country",
          "Ship_zip_code",
          "Ship_state",
          "Ship_address_2",
          "Ship_address_1",
        ];
      case 3:
        return [];
      default:
        return [];
    }
  };

  useEffect(() => {
    const newFormId = generateUniqueId();
    setFormId(newFormId);
    reset();
  }, []);

  const handleStepChange = useCallback(
    (type) => {
      if (type === "next") {
        const currentData = methods.getValues();
        console.log("currentData", currentData);
        const fieldsToValidate = getFieldsForCurrentStep(currentStep);
        methods.trigger(fieldsToValidate).then((isValid) => {
          if (isValid) {
            if (currentStep <= steps.length - 1) {
              setCurrentStep((prev) => prev + 1);
            }
          } else {
            console.log("Errors:", methods.formState.errors);
          }
        });
      } else {
        console.log("Errors:2", methods.formState.errors);
        if (currentStep > 1) {
          setCurrentStep((prev) => prev - 1);
        }
      }
    },
    [currentStep, methods]
  );

//   useEffect(() => {
//     const handleKeyDown = (event) => {
//       if (event.key === "ArrowRight") {
//         handleStepChange("next");
//       } else if (event.key === "ArrowLeft") {
//         handleStepChange("prev");
//       }
//     };

//     document.addEventListener("keydown", handleKeyDown);
//     return () => {
//       document.removeEventListener("keydown", handleKeyDown);
//     };
//   }, [handleStepChange]);

  const onError = (error) => {
    toast.error(error?.message);
  };

  const onSubmit = (data) => {
    console.log("data", data);

    const payload = {
      token: token,
      attachment_id: formId,
      display_name: data.display_name || null,
      type: data.CustomerType,
      first_name: data.first_name || null,

      last_name: data.last_name || null,
      address: data.Address || null,
      // print_check_name: data.print_check_name || null,

      primary_email: data.primary_email
        ? JSON.stringify({ Address: data.primary_email })
        : null,

      primary_phone: data.primary_phone
        ? JSON.stringify({ FreeFormNumber: data.primary_phone })
        : null,

      billing_address:
        data.Bill_city ||
        data.Bill_address_1 ||
        data.Bill_address_2 ||
        data.Bill_country ||
        data.Bill_zip_code ||
        data.Bill_state
          ? JSON.stringify({
              Id: customer?.data?.billing_address?.Id,
              City: data.Bill_city ? data.Bill_city : null,
              Line1: data.Bill_address_1 ? data.Bill_address_1 : null,
              Line2: data.Bill_address_2 ? data.Bill_address_1 : null,
              Country: data.Bill_country ? data.Bill_country : null,
              PostalCode: data.Bill_zip_code ? data.Bill_zip_code : null,
              CountrySubDivisionCode: data.Bill_state ? data.Bill_state : null,
            })
          : null,

      billing_name: data.billing_name,
      billing_email: data.billing_email,
      billing_phone: data.billing_phone,

      shipping_name:
        data.sameAddress === true ? data.billing_name : data.shipping_name,
      shipping_email:
        data.sameAddress === true ? data.billing_email : data.shipping_email,
      shipping_phone:
        data.sameAddress === true ? data.billing_phone : data.shipping_phone,

      shipping_address:
        data.sameAddress === true
          ? data.Bill_city ||
            data.Bill_address_1 ||
            data.Bill_address_2 ||
            data.Bill_country ||
            data.Bill_zip_code ||
            data.Bill_state
            ? JSON.stringify({
                Id: customer?.data?.billing_address?.Id,
                City: data.Bill_city ? data.Bill_city : null,
                Line1: data.Bill_address_1 ? data.Bill_address_1 : null,
                Line2: data.Bill_address_2 ? data.Bill_address_2 : null,
                Country: data.Bill_country ? data.Bill_country : null,
                PostalCode: data.Bill_zip_code ? data.Bill_zip_code : null,
                CountrySubDivisionCode: data.Bill_state
                  ? data.Bill_state
                  : null,
              })
            : null
          : data.Ship_city ||
            data.Ship_address_1 ||
            data.Ship_address_2 ||
            data.Ship_country ||
            data.Ship_zip_code ||
            data.Ship_state
          ? JSON.stringify({
              Id: customer?.data?.shipping_address?.Id,
              City: data.Ship_city ? data.Ship_city : null,
              Line1: data.Ship_address_1 ? data.Ship_address_1 : null,
              Line2: data.Ship_address_2 ? data.Ship_address_2 : null,
              Country: data.Ship_country ? data.Ship_country : null,
              PostalCode: data.Ship_zip_code ? data.Ship_zip_code : null,
              CountrySubDivisionCode: data.Ship_state ? data.Ship_state : null,
            })
          : null,

      web_addr: data.web_addr
        ? JSON.stringify({ URI: data.web_addr ? data.web_addr : null })
        : null,
    };

    const formData = new FormData();

    if (data?.files && data?.files.length > 0) {
      data.files.forEach((file, index) => {
        formData.append("customer", file);
      });
    }

    // console.log(payload, "payload");
    const onSuccess = (res) => {
      formData.append("auto_id", res?.auto_id);
      if (data?.files && data?.files.length > 0) {
        uploadImage(formData, API_ENDPOINTS.UPLOAD_ATTACHMENTS);
      }
      // UPLOAD_ATTACHMENTS
      toast.success(res?.message);
      reset();
      setShowSuccessModal(true);
    };
    if (customer && customer?.data?.quickbook_id == 0) {
      WihtouthQuickBook(JSON.stringify(payload), onSuccess, onError);
    } else {
      postData(JSON.stringify(payload), onSuccess, onError);
    }
  };

  if (tokenError) {
    return (
      <ErrorModal isOpen={true} errorMessage={tokenError} redirectPath="/" />
    );
  }

  return (
    <>
      <ComanConfirmationModal
        show={isModalCnfirmOpen.show}
        onHide={() => setIsModalCnfirmOpen({ ...isModalOpen, show: false })}
        title={isModalCnfirmOpen.title}
        message={isModalCnfirmOpen.message}
        onConfirm={isModalCnfirmOpen.onConfirm}
        icon={isModalCnfirmOpen.icon}
        type={isModalCnfirmOpen.type}
      />

      <SuccsessMsgModal
        show={isModalOpen.show}
        onHide={() => {
          setIsModalOpen({ ...isModalOpen, show: false });
          navigate("/customer-list");
        }}
        title={isModalOpen.title}
        btntext={isModalOpen.btntext}
        message={isModalOpen.message}
      />

      <div
        style={{
          backgroundImage: `url(${auth_background})`,
          height: "100vh",
        }}
        className="full_modal_bg"
      >
        <Container>
          <Card className="mt-4">
            <Card.Header className="">
              <h4> Create Customer</h4>
            </Card.Header>
            <Card.Body>
              <div className="page-header d-block d-sm-none text-center">
                <h2 className="page-title justify-content-center">
                  Create Customer
                </h2>
              </div>
              <Stepper steps={steps} currentStep={currentStep} />

              <FormProvider
                {...methods}
                className=""
                onSubmit={methods.handleSubmit(onSubmit)}
              >
                <Form onSubmit={methods.handleSubmit(onSubmit)}>
                  {currentStep === 1 && (
                    <GeneralStep Type={Type} setType={setType} />
                  )}
                  {currentStep === 2 && <BillingInfo />}
                  {currentStep === 3 && (
                    <FileUpload moduleType={"CUSTOMER"} attachmnetid={formId} />
                  )}

                  <div className="d-flex justify-content-center gap-3 mt-3">
                    {currentStep > 1 && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => handleStepChange("prev")}
                      >
                        <IconArrowNarrowLeftDashed /> Back
                      </Button>
                    )}
                    {currentStep < 3 && (
                      <Button
                        variant="warning"
                        onClick={() => handleStepChange("next")}
                      >
                        Next <IconArrowNarrowRightDashed />
                      </Button>
                    )}

                    {currentStep === 3 && (
                      <div className="d-flex">
                        <Button variant="warning" type="submit">
                          {createLoading ? (
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                          ) : (
                            "Submit"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </Form>
              </FormProvider>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {showSuccessModal && <SuccessModal isOpen={showSuccessModal} />}
    </>
  );
}

export default CreateCustomer;
